import { useEffect, useRef, useState } from "react";
import { Clock, ChevronDown, Sparkles } from "lucide-react";
import { useSiteFlags } from "@/context/SiteFlagsContext";

const Lista = ({ servicios, onClose }) => (
  <>
    {servicios.map((s, i) => (
      <div key={s} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100" data-testid={`proximos-item-${i}`}>
        <Sparkles size={14} className="text-[#F5B301] shrink-0" />
        <span className="text-sm font-semibold text-[#13315C] flex-1">{s}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#9a7100] bg-[#F5B301]/15 rounded-full px-2 py-0.5">Pronto</span>
      </div>
    ))}
    <a href="/#proximamente" onClick={onClose} className="block px-4 py-3 text-xs font-bold text-[#13315C] hover:bg-slate-50" data-testid="proximos-ver-mas">Ver todos los próximos servicios →</a>
  </>
);

const useProximos = () => {
  const { proximamente: p } = useSiteFlags();
  return p?.visible && p.servicios?.length ? p.servicios : null;
};

/** Botón desplegable «Próximos servicios» del menú de escritorio. */
export const ProximosDesktop = ({ transparent }) => {
  const servicios = useProximos();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  if (!servicios) return null;
  return (
    <div className="relative hidden sm:block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-semibold transition active:scale-[0.96] ${
          transparent ? "border-white/20 text-white/90 hover:border-[#F5B301]" : "border-slate-200 text-[#13315C] hover:border-[#F5B301]"}`}
        data-testid="nav-proximos-btn">
        <Clock size={14} className="text-[#F5B301]" /> Próximos servicios <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-2 w-[320px] z-50" data-testid="nav-proximos-menu">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <p className="px-4 pt-3 pb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">Próximamente disponible</p>
            <Lista servicios={servicios} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

/** Entrada desplegable «Próximos servicios» del menú móvil (en lugar de «Escanear QR»). */
export const ProximosMobile = ({ onClose }) => {
  const servicios = useProximos();
  const [open, setOpen] = useState(false);
  if (!servicios) return null;
  return (
    <div data-testid="mob-proximos">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 min-h-[44px] text-left"
        data-testid="mob-proximos-btn">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-[#F5B301]/10 text-[#13315C] shrink-0"><Clock size={16} /></span>
        <span className="text-sm font-semibold text-[#13315C] flex-1">Próximos servicios</span>
        <ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="ml-3 rounded-xl border border-slate-100 bg-white overflow-hidden" data-testid="mob-proximos-lista"><Lista servicios={servicios} onClose={onClose} /></div>}
    </div>
  );
};

